/**
 * 견적서 → 주요제품 판매가(가격표) 추가 모달.
 *
 * 견적서 미리보기에서 [➕ 가격표에 추가] 클릭 시 표시. 대분류(스토리지 / 서버 / SW / 기타)를
 * 고르고, 견적 항목을 검토·수정한 뒤 선택한 항목을 가격표에 일괄 추가한다.
 *
 * 단가 매핑 (사용자 확정): 견적 '공급단가' → 가격표 '공급단가'. 소비자단가는 0(빈칸)으로
 * 채워두고 필요 시 여기서 직접 입력.
 */

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QuoteDetail } from '../../lib/quote-api';
import { bulkCreateMainProducts, MAIN_PRICE_CATEGORIES, type MainProductInput } from '../../lib/price-list-api';
import { DraggableModal } from '../../components/DraggableModal';

interface Props {
  quote: QuoteDetail;
  onClose: () => void;
  onDone: (count: number) => void;
}

/** 대분류 4가지 (v2.230.0 · 가격표 화면·서버와 같은 목록) */
const CATEGORIES = MAIN_PRICE_CATEGORIES;

interface DraftRow {
  checked: boolean;
  name: string;
  description: string;
  retailPrice: number;
  supplyPrice: number;
  note: string;
}

export function AddQuoteToPriceListModal({ quote, onClose, onDone }: Props) {
  const qc = useQueryClient();
  const [category, setCategory] = useState<string>('스토리지');
  const [rows, setRows] = useState<DraftRow[]>(() =>
    quote.items.map((it) => ({
      checked: true,
      name: it.itemName,
      description: it.description,
      retailPrice: 0,
      supplyPrice: it.unitPrice,
      // 견적서 비고는 여러 줄일 수 있다(v2.325.1) — 가격표 비고는 한 줄 칸이라 줄바꿈을 띄어쓰기로
      note: (it.note ?? '').replace(/\s*\n\s*/g, ' ').trim(),
    })),
  );

  const bulkMut = useMutation({
    mutationFn: (items: MainProductInput[]) => bulkCreateMainProducts(items),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['price-list', 'main'] });
      onDone(res.count);
    },
  });

  const checkedCount = rows.filter((r) => r.checked && r.name.trim()).length;

  function patchRow(idx: number, patch: Partial<DraftRow>) {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  function toggleAll(checked: boolean) {
    setRows((prev) => prev.map((r) => ({ ...r, checked })));
  }

  function onSubmit() {
    const items: MainProductInput[] = rows
      .filter((r) => r.checked && r.name.trim())
      .map((r) => ({
        category,
        name: r.name.trim(),
        description: r.description,
        retailPrice: r.retailPrice || 0,
        supplyPrice: r.supplyPrice || 0,
        note: r.note.trim() || null,
      }));
    if (items.length === 0) {
      window.alert('추가할 항목을 1개 이상 선택하세요.');
      return;
    }
    bulkMut.mutate(items);
  }

  const saving = bulkMut.isPending;
  const allChecked = rows.length > 0 && rows.every((r) => r.checked);

  return (
    <DraggableModal onClose={onClose} initialWidth={900} closeOnBackdrop={false} closeOnEsc={false}>
      <div className="dm-drag-handle cursor-move flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
        <div>
          <h2 className="ui-modal-title">가격표에 추가 — 주요제품 판매가</h2>
          <div className="text-xs text-slate-500 mt-0.5">
            견적서 {quote.quoteNo} · {quote.projectName || '제목 없음'}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="ui-close"
        >
          ×
        </button>
      </div>

      <div className="p-6 space-y-4 text-sm">
        {/* 대분류 선택 */}
        <div className="flex items-center gap-3">
          <label className="ui-label whitespace-nowrap">대분류</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="ui-input"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <span className="text-xs text-slate-500">
            선택한 항목이 이 대분류로 &ldquo;주요제품 판매가&rdquo; 목록에 추가됩니다.
          </span>
        </div>

        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2">
          견적서의 <b>공급단가</b>가 가격표의 <b>공급단가</b>로 채워졌습니다. 소비자단가는
          비어 있으니 필요하면 직접 입력하세요. (수량은 가격표에 없어 제외됩니다.)
        </div>

        {/* 항목 표 */}
        <div className="overflow-x-auto border border-slate-200 rounded">
          <table className="w-full text-xs">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-2 py-1.5 w-8 text-center">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={(e) => toggleAll(e.target.checked)}
                    title="전체 선택/해제"
                  />
                </th>
                <th className="px-2 py-1.5 text-left w-40">품목명 *</th>
                <th className="px-2 py-1.5 text-left">상세내용</th>
                <th className="px-2 py-1.5 text-right w-28">소비자단가</th>
                <th className="px-2 py-1.5 text-right w-28">공급단가</th>
                <th className="px-2 py-1.5 text-left w-28">비고</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr
                  key={idx}
                  className={'border-t border-slate-100 ' + (r.checked ? '' : 'opacity-40')}
                >
                  <td className="px-2 py-1.5 text-center align-top">
                    <input
                      type="checkbox"
                      checked={r.checked}
                      onChange={(e) => patchRow(idx, { checked: e.target.checked })}
                    />
                  </td>
                  <td className="px-2 py-1.5 align-top">
                    <input
                      value={r.name}
                      onChange={(e) => patchRow(idx, { name: e.target.value })}
                      className="ui-input ui-input-sm w-full"
                    />
                  </td>
                  <td className="px-2 py-1.5 align-top">
                    <textarea
                      value={r.description}
                      onChange={(e) => patchRow(idx, { description: e.target.value })}
                      rows={2}
                      className="ui-input ui-textarea w-full font-mono resize-y min-h-[2.2rem]"
                    />
                  </td>
                  <td className="px-2 py-1.5 align-top">
                    <input
                      type="number"
                      min={0}
                      value={r.retailPrice}
                      onChange={(e) => patchRow(idx, { retailPrice: Number(e.target.value) || 0 })}
                      className="ui-input ui-input-sm w-full text-right num"
                    />
                  </td>
                  <td className="px-2 py-1.5 align-top">
                    <input
                      type="number"
                      min={0}
                      value={r.supplyPrice}
                      onChange={(e) => patchRow(idx, { supplyPrice: Number(e.target.value) || 0 })}
                      className="ui-input ui-input-sm w-full text-right num"
                    />
                  </td>
                  <td className="px-2 py-1.5 align-top">
                    <input
                      value={r.note}
                      onChange={(e) => patchRow(idx, { note: e.target.value })}
                      className="ui-input ui-input-sm w-full"
                    />
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-6 text-slate-400">
                    견적 항목이 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {bulkMut.error && (
          <div className="bg-red-50 border border-red-200 rounded px-3 py-2 text-xs text-red-700">
            {(bulkMut.error as Error).message}
          </div>
        )}
      </div>

      <div className="flex justify-end items-center gap-2 px-6 py-4 border-t border-slate-200 bg-slate-50">
        <span className="text-xs text-slate-500 mr-auto">선택 {checkedCount}건</span>
        <button
          type="button"
          onClick={onClose}
          className="btn btn-secondary"
        >
          취소
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={saving || checkedCount === 0}
          className="btn btn-primary"
        >
          {saving ? '추가 중…' : `${checkedCount}건 가격표에 추가`}
        </button>
      </div>
    </DraggableModal>
  );
}
